import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { X, CircleAlert, Mail } from '@/components/icons'
import { Button } from '../ui/button'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '../ui/drawer'
import { PrntrCard } from '../ui/prntr-input'
import { SelectRowField, SwitchRowField, TextRow } from '../ui/prntr-fields'
import { toast } from 'sonner'
import { useCrewQuickAdd } from '../../lib/hooks/crew'
import { useQueryClient } from '@tanstack/react-query'
import { useOrpcTanstackQueryUtils } from '../../lib/orpc'
import { CrewRole, crewRoles, crewRoleLabels } from '@@api/shared'

interface AddedCrew {
  firstName: string
  lastName: string
  avatarColor: string
}

const quickCrewSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
  crewRole: z.nativeEnum(CrewRole).nullable().optional(),
  isAdmin: z.boolean().default(false),
  shouldSendInvitation: z.boolean().default(true),
})

type QuickCrewForm = z.infer<typeof quickCrewSchema>

interface QuickStartCrewDrawerProps {
  isOpen: boolean
  onClose: () => void
}

export function QuickStartCrewDrawer({ isOpen, onClose }: QuickStartCrewDrawerProps) {
  const [addedCrew, setAddedCrew] = useState<AddedCrew[]>([])
  const crewQuickAdd = useCrewQuickAdd()
  const queryClient = useQueryClient()
  const orpc = useOrpcTanstackQueryUtils()

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isSubmitting },
  } = useForm<QuickCrewForm>({
    resolver: zodResolver(quickCrewSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      crewRole: null,
      isAdmin: false,
      shouldSendInvitation: true,
    },
    mode: 'onChange',
  })

  const handleSave = async (data: QuickCrewForm, addAnother: boolean) => {
    let created
    try {
      created = await crewQuickAdd.mutateAsync({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        crewRole: data.crewRole,
        isAdmin: data.isAdmin,
        shouldSendInvitation: data.shouldSendInvitation,
      })
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to add crew member')
      return
    }

    setAddedCrew((prev) => [
      ...prev,
      { firstName: data.firstName, lastName: data.lastName, avatarColor: created.avatarColor },
    ])

    if (addAnother) {
      reset()
    } else {
      queryClient.invalidateQueries({ queryKey: orpc.onboarding.key() })
      handleClose()
    }
  }

  const handleClose = () => {
    reset()
    setAddedCrew([])
    onClose()
  }

  const getInitials = (firstName: string, lastName: string) =>
    `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DrawerContent className="bg-surface-L0 flex flex-col max-h-[85dvh]">
        <DrawerHeader className="flex items-center justify-between p-4 shrink-0">
          <DrawerTitle className="text-lg font-bold text-content-primary-bold tracking-[-1px]">
            Add Crew Members
          </DrawerTitle>
          <button type="button" onClick={handleClose} className="p-1 hover:bg-surface-L2 rounded transition-colors">
            <X className="w-4 h-4 text-content-primary-bold" />
          </button>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto px-2" style={{ WebkitOverflowScrolling: 'touch' }}>
          {/* Info banner */}
          <div className="bg-surface-L2 rounded-md flex gap-3 items-center px-3 py-2 mb-2">
            <CircleAlert className="w-5 h-5 text-content-primary-subtle shrink-0" />
            <p className="text-sm text-content-primary-subtle tracking-[0.07px] leading-normal">
              Invite crew with a one-time code. You can add more details and upload documents later.
            </p>
          </div>

          {/* Added crew avatars */}
          {addedCrew.length > 0 && (
            <div className="flex items-center pl-2 pr-4 py-4">
              {addedCrew.map((crew, i) => (
                <div
                  key={i}
                  className="size-8 rounded-full flex items-center justify-center text-sm font-medium text-white -mr-2"
                  style={{ backgroundColor: crew.avatarColor }}
                >
                  {getInitials(crew.firstName, crew.lastName)}
                </div>
              ))}
            </div>
          )}

          {/* Form */}
          <div className="flex flex-col gap-3 p-2">
            <PrntrCard title="Details">
              <TextRow nullable={false}
                control={control}
                name="firstName"
                label="First Name *"
                placeholder="First name"
              />
              <TextRow nullable={false}
                control={control}
                name="lastName"
                label="Last Name *"
                placeholder="Last name"
              />
              <TextRow nullable={false}
                control={control}
                name="email"
                label="Email Address *"
                type="email"
                placeholder="email@example.com"
                iconLeft={<Mail />}
              />
              <SelectRowField
                control={control}
                name="crewRole"
                label="Role"
                options={crewRoles.map((role) => ({ value: role, label: crewRoleLabels[role] }))}
                placeholder="Select role"
              />
            </PrntrCard>

            <PrntrCard title="Access">
              <SwitchRowField
                control={control}
                name="isAdmin"
                label={
                  <span className="flex flex-col">
                    Make user Admin
                    <span className="text-xs font-normal text-content-primary-subtle">
                      User will have full admin rights
                    </span>
                  </span>
                }
              />
              <SwitchRowField
                control={control}
                name="shouldSendInvitation"
                label={
                  <span className="flex flex-col">
                    Send an invitation
                    <span className="text-xs font-normal text-content-primary-subtle">
                      Email an invitation to join the platform
                    </span>
                  </span>
                }
              />
            </PrntrCard>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 shrink-0">
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              className="flex-1"
              disabled={!isValid || isSubmitting}
              onClick={handleSubmit((data) => handleSave(data, true))}
            >
              Save & Add Another
            </Button>
            <Button
              variant="primary"
              size="sm"
              className="flex-1"
              disabled={!isValid || isSubmitting}
              onClick={handleSubmit((data) => handleSave(data, false))}
            >
              Save & Continue Setup
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
